import { usePersistedState } from "@/lib/persistedFilters";
// PaymentsTimeline — all-partners payment history for staff. Search by
// partner/method/reference/notes; filter by date range or period; export
// bank-ready CSV for reconciliation.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, DollarSign, Search, Download, Calendar, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PaymentDialog } from "@/components/commissions/PaymentDialog";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const decodeHtml = (s) => (s || "")
  .replace(/&#(\d+);/g,  (_, n) => String.fromCharCode(parseInt(n, 10)))
  .replace(/&#x([0-9a-f]+);/gi, (_, h) => String.fromCharCode(parseInt(h, 16)))
  .replace(/&amp;/g, "&").replace(/&quot;/g, "\"").replace(/&apos;/g, "'")
  .replace(/&lt;/g, "<").replace(/&gt;/g, ">");

export default function PaymentsTimeline() {
  const navigate = useNavigate();
  const [q, setQ]         = useState("");
  const [frm, setFrm]     = useState("");
  const [to, setTo]       = useState("");
  const [period, setPeriod] = usePersistedState("payments.period", "");
  const [items, setItems] = useState([]);
  const [totalPaid, setTotalPaid] = useState(0);
  const [loading, setLoading] = useState(false);
  // Iter 306 — record a payment for any partner straight from the history.
  const [recordOpen, setRecordOpen] = useState(false);
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const openRecord = async () => {
    if (!partnerOptions.length) {
      try { const { data } = await api.get("/commissions/payments/partner-options"); setPartnerOptions(data.items || []); }
      catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load partners"); return; }
    }
    setRecordOpen(true);
  };
  const recordPayment = async (form) => {
    setSaving(true);
    try {
      await api.post(`/commissions/partners/${encodeURIComponent(form.partner_key)}/payments`, {
        period_label: form.period_label, amount: Number(form.amount || 0), method: form.method || "",
        reference: form.reference || "", paid_at: form.paid_at || null, notes: form.notes || "",
      });
      toast.success("Payment recorded"); setRecordOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to record payment"); } finally { setSaving(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/commissions/payments", { params: {
        q: q || undefined,
        frm: frm || undefined,
        to: to || undefined,
        period_label: period || undefined,
      } });
      setItems(data.items || []);
      setTotalPaid(data.total_paid || 0);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load payments");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (frm)    params.set("frm", frm);
    if (to)     params.set("to", to);
    if (period) params.set("period_label", period);
    window.open(`${api.defaults.baseURL}/commissions/payments/export.csv?${params.toString()}`, "_blank");
  };

  const grouped = useMemo(() => {
    const g = new Map();
    for (const p of items) {
      const d = (p.paid_at || "").slice(0, 10);
      if (!g.has(d)) g.set(d, []);
      g.get(d).push(p);
    }
    return Array.from(g.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [items]);

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6" data-testid="payments-timeline-page">
      <div className="flex items-center gap-2 mb-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/commissions")}
                data-testid="payments-back" className="h-8 -ml-2">
          <ArrowLeft className="h-4 w-4" /> Back to Commissions
        </Button>
      </div>
      <PageHeader testid="payments-header" title="Payment history" icon={DollarSign}
        subtitle="Every commission payment recorded across every partner.">
        <Button onClick={openRecord} data-testid="payments-record" className="bg-primary text-primary-foreground hover:bg-primary/90">
          <Plus className="h-4 w-4" /> Record payment
        </Button>
        <Button variant="outline" onClick={exportCsv} data-testid="payments-export-csv">
          <Download className="h-4 w-4" /> Export CSV
        </Button>
      </PageHeader>

      <Card className="border-slate-200 shadow-sm p-3 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[240px]">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Search</Label>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                     onKeyDown={(e) => e.key === "Enter" && load()}
                     placeholder="Partner, method, reference, notes…"
                     className="pl-7 h-9" data-testid="payments-search" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Period</Label>
            <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)}
                   className="h-9 w-[140px]" data-testid="payments-period" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">From</Label>
            <Input type="date" value={frm} onChange={(e) => setFrm(e.target.value)}
                   className="h-9 w-[150px]" data-testid="payments-from" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                   className="h-9 w-[150px]" data-testid="payments-to" />
          </div>
          <Button onClick={load} disabled={loading} data-testid="payments-apply-filters"
                  className="h-9 bg-primary text-primary-foreground hover:bg-primary/90">
            Apply
          </Button>
          <Button variant="ghost" onClick={() => { setQ(""); setFrm(""); setTo(""); setPeriod(""); load(); }}
                  className="h-9 text-slate-500" data-testid="payments-clear-filters">
            Clear
          </Button>
          <div className="ml-auto text-xs text-slate-500 self-center">
            <b className="text-slate-800 text-sm tabular-nums">{items.length}</b> payments ·
            <span className="ml-2 text-slate-500">Total </span>
            <b className="text-emerald-700 tabular-nums text-sm">{money(totalPaid)}</b>
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="py-16 text-center text-slate-400">Loading…</div>
      ) : grouped.length === 0 ? (
        <div className="py-16 text-center text-slate-400" data-testid="payments-empty">
          No payments match the current filters.
        </div>
      ) : (
        <div className="space-y-3">
          {grouped.map(([date, list]) => (
            <Card key={date} className="border-slate-200 shadow-sm overflow-hidden">
              <div className="flex items-center justify-between bg-slate-50/70 border-b border-slate-100 px-3 py-2">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-slate-400" />
                  <span className="text-sm font-semibold text-slate-700">{date || "Undated"}</span>
                  <span className="text-xs text-slate-500">· {list.length} payment{list.length !== 1 ? "s" : ""}</span>
                </div>
                <span className="text-xs text-emerald-700 font-medium tabular-nums">
                  {money(list.reduce((s, p) => s + (Number(p.amount) || 0), 0))}
                </span>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-white text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Partner</th>
                    <th className="px-3 py-2 text-left">Period</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2 text-left">Method</th>
                    <th className="px-3 py-2 text-left">Reference</th>
                    <th className="px-3 py-2 text-left">Notes</th>
                    <th className="px-3 py-2 text-left">Recorded by</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {list.map((p) => (
                    <tr key={p.payment_id} className="hover:bg-slate-50/50"
                        data-testid={`timeline-payment-${p.payment_id}`}>
                      <td className="px-3 py-2">
                        <button onClick={() => navigate(`/commissions/partners/${encodeURIComponent(p.partner_key)}`)}
                                className="text-slate-700 hover:text-primary hover:underline font-medium"
                                data-testid={`timeline-partner-link-${p.payment_id}`}>
                          {decodeHtml(p.partner_key)}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500">{p.period_label}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-700 font-medium">{money(p.amount)}</td>
                      <td className="px-3 py-2 text-xs">{p.method || <span className="text-slate-300">—</span>}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{p.reference || <span className="text-slate-300">—</span>}</td>
                      <td className="px-3 py-2 text-xs text-slate-500 truncate max-w-[300px]" title={p.notes}>
                        {decodeHtml(p.notes) || <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500">{p.recorded_by_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          ))}
        </div>
      )}
      {recordOpen && (
        <PaymentDialog partnerOptions={partnerOptions} saving={saving}
                       onClose={() => setRecordOpen(false)} onSubmit={recordPayment} />
      )}
    </div>
  );
}
